import { useCallback } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { useReprocessOrder } from "@/management/hooks/useReprocessOrder"
import { MANAGEMENT } from "@/constants"
import { useModal, useModalActions } from "@/shared/hooks"
import { ModalType, RouteParams } from "@/shared/types"
import { showToast } from "@/shared/utils"

import { Button, Copy, Flex, Stack, Title } from "@/shared/components"
import { GeneralModal } from "@/modals"

export const ReprocessOrderModal = () => {
  const { orderId, organizationId } = useParams<keyof RouteParams>() as RouteParams
  const navigate = useNavigate()
  const { modalProps } = useModal(ModalType.ReprocessOrderModal + orderId)
  const { close } = useModalActions()
  const { mutateAsync: reprocessOrder, isLoading } = useReprocessOrder()

  const handleContinueClick = useCallback(async () => {
    try {
      const result = await reprocessOrder(orderId)
      close(ModalType.ReprocessOrderModal + orderId)

      const validationErrors = result.validationErrors?.trim()
      if (validationErrors) {
        showToast({ type: "error", text: validationErrors })
      } else {
        showToast({ type: "success", text: "Order reprocessed successfully." })
      }

      navigate(`${MANAGEMENT}/organization/${organizationId}/order/${result.orderId}`)
    } catch {
      close(ModalType.ReprocessOrderModal + orderId)
    }
  }, [close, navigate, orderId, organizationId, reprocessOrder])

  return (
    <GeneralModal
      {...modalProps}
      footer={
        <Flex justify="end" css={{ gap: "$16", paddingBottom: "$8" }}>
          <Button action="primary" onClick={handleContinueClick} loading={isLoading}>
            Continue
          </Button>
          <Button
            action="secondary"
            onClick={() => close(ModalType.ReprocessOrderModal + orderId)}
            disabled={isLoading}
          >
            Cancel
          </Button>
        </Flex>
      }
    >
      <Stack space={12} css={{ padding: "$64 $24 $0" }}>
        <Title as="h3" scale={2} color="system-black">
          Reprocess order?
        </Title>
        <Copy scale={10} color="theme-n6-n5" css={{ paddingBottom: "$8" }}>
          This will create a new order version and restart tendering. Continue?
        </Copy>
      </Stack>
    </GeneralModal>
  )
}
